import _ from "lodash";
import * as DateUtils from "../../../../../../utils/date/date";
import { BrainTools } from "../../BrainTools/BrainTools";
import { CalcOperation } from "../../Operators/CalcOperations";
import { MathOperations } from "../../Operators/MathOperations";

/**
 * @class
 * Responsável pela processamento da criação de uma respota para Contábil
 */
export class AccountingMachine {
    constructor({ Serie } = {}) {
        this.Tools = new BrainTools();

        this.Serie = Serie;

        this.constants = {
            MES: true,
            QUANTIDADE: true
        };

        this.periods = {
            monthly: { count: 1 },
            quarterly: { count: 3, months: [3, 6, 9, 12] },
            semester: { count: 6, months: [6, 12] },
            yearly: { count: 12, months: [12] }
        };

        this.availableMonthsIndex = {
            monthly: [],
            quarterly: [
                [1, 2, 3],
                [4, 5, 6],
                [7, 8, 9],
                [10, 11, 12]
            ],
            semester: [
                [1, 2, 3, 4, 5, 6],
                [7, 8, 9, 10, 11, 12]
            ],
            yearly: [[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]]
        };

        this._setup();
    }

    run(
        serie,
        points,
        scenario,
        period,
        timesAvailable,
        opts = {},
        _source,
        totalCompanies
    ) {
        const pointsSorted = _.sortBy(points, ["t"]);

        timesAvailable = !timesAvailable
            ? Object.keys(_.groupBy(pointsSorted, "t"))
            : timesAvailable;

        const pointsCalculated = [];

        const monthsArr = _.get(this.periods, `${period}.months`, []);

        const effects = {
            reducing: _.get(serie.metadata, "reducing"),
            positive: _.get(serie.metadata, "positive"),
            negative: _.get(serie.metadata, "negative")
        };

        const serieMinValue = _.isNumber(_.get(serie.metadata, "minValue"))
            ? serie.metadata.minValue
            : null;

        const hasSerieMin = _.isNumber(_.get(serie.metadata, "minValue"))
            ? true
            : false;

        for (let i in timesAvailable) {
            let timeAvailable = timesAvailable[i];

            let time = 0;

            if (timeAvailable !== 0 && timeAvailable !== "0")
                time = DateUtils.getDate(timeAvailable);

            let year = time !== 0 ? time.getFullYear() : null;

            if (
                time !== 0 &&
                (period === "monthly" ||
                    monthsArr.includes(
                        parseInt(DateUtils.dateToString(time, "M"))
                    ))
            ) {
                const availableMonthListIdx = this.availableMonthsIndex[
                    period
                ].findIndex(
                    (idx) =>
                        idx &&
                        idx.includes(
                            parseInt(DateUtils.dateToString(time, "M"))
                        )
                );

                let exp = this.Tools.rulesRemove(serie.exp);

                const variables = this.Tools.expressionSplit(serie.exp);

                for (let j in variables) {
                    let currentPoint = {};
                    let pointsByYear = [];
                    let variable = variables[j];
                    const rule = this.Tools.ruleExtract(variable);

                    variable = this.Tools.serieIdExtract(variable);

                    const regex = this.Tools.serieExchangeReference(variable);

                    variable = this.Tools.specialCharacterRemove(variable);

                    for (let i in pointsSorted) {
                        let point = pointsSorted[i];
                        if (
                            (point.t === "0" ||
                                point.t === 0 ||
                                point.t.getTime() == time.getTime()) &&
                            variable === point.s
                        ) {
                            currentPoint = point;
                            break;
                        }
                    }

                    if (!currentPoint.isConstant) {
                        pointsByYear = _.filter(
                            pointsSorted,
                            (p) =>
                                year &&
                                p.s === currentPoint.s &&
                                p.t.getFullYear() === year
                        );
                    }

                    let value = this._getValueByCondition({
                        time,
                        availableIdx: availableMonthListIdx,
                        rule,
                        variable,
                        scenario,
                        period,
                        serie,
                        currentSerieId: currentPoint.s,
                        currentPoint: currentPoint.m,
                        points: pointsByYear,
                        opts,
                        totalCompanies
                    });

                    // if (value) value = value.toFixed(2);
                    if (isNaN(value)) value = 0;

                    exp = exp.replace(regex, value);
                }

                exp = exp.replace(/\[|\]/g, "");

                const expValue = MathOperations(exp);

                let expResult = 0;

                if (hasSerieMin) {
                    expResult =
                        expValue > serieMinValue ? expValue : serieMinValue;
                } else if (isFinite(expValue)) {
                    expResult = expValue;
                }

                pointsCalculated.push({
                    t: time,
                    s: serie._id,
                    c: serie.companyId,
                    sc: scenario,
                    u: DateUtils.getDate(new Date()),
                    m: { v: expResult },
                    period,
                    ...effects
                });
            }
        }

        return pointsCalculated;
    }

    _setup() {
        const accountingCalculate = {
            ORIGINAL: this._originalScenarioPointsCalculate,
            custom: this._customScenarioPointsCalculate
        };

        const handler = {
            get: (target, name) => {
                return target.hasOwnProperty(name)
                    ? target[name]
                    : target["custom"];
            }
        };

        this.accountingProxy = new Proxy(accountingCalculate, handler);

        const { original, scenario } = {
            original: {
                i: this._originalTemporalRuleFunction,
                v: this._originalValueRuleFunction,
                custom: this._originalDefaultRuleFunction
            },
            scenario: {
                i: this._scenarioTemporalRuleFunction,
                v: this._scenarioValueRuleFunction,
                custom: this._scenarioDefaultRuleFunction
            }
        };

        this.originalRulesProxy = new Proxy(original, handler);

        this.scenarioRulesProxy = new Proxy(scenario, handler);
    }

    /**
     * @private
     * Realiza tratamento dos values de points de acordo com uma condição de calculo
     * @param {Object} point point que ira receber uma condição
     * @param {Array} points todos os points usados no kpi, para realizar tratamentos
     */
    _getValueByCondition({
        time,
        availableIdx,
        serie,
        currentPoint,
        currentSerieId,
        points,
        variable,
        scenario,
        period,
        rule,
        opts,
        totalCompanies
    } = {}) {
        const isAccumulated = _.get(serie, "metadata.accumulated", false);

        const monthPeriod = isAccumulated ? 1 : this.periods[period].count;

        const currentSerie =
            this.Serie.findOneCollection({
                _id: currentSerieId
            }) || {};

        if (this.constants[variable])
            return CalcOperation.isConstant(variable, time, points);

        if (!currentPoint) return 0;

        if (
            this.periods[period].months &&
            !_.includes(
                this.periods[period].months,
                parseInt(DateUtils.dateToString(time, "M"))
            )
        ) {
            return 0;
        }

        const params =
            scenario === "ORIGINAL"
                ? {
                      period,
                      time,
                      availableIdx,
                      monthPeriod,
                      rule,
                      currentSerieId,
                      points,
                      currentPoint,
                      opts
                  }
                : {
                      period,
                      time,
                      availableIdx,
                      monthPeriod,
                      rule,
                      currentSerieId,
                      points,
                      currentPoint,
                      isAccumulated,
                      opts
                  };

        let val = this.accountingProxy[scenario](params);

        if (
            !currentSerie.groupId &&
            opts.avg &&
            serie.groupId &&
            !serie.metadata.kpi &&
            currentSerie.unit != "percentage"
        ) {
            val = +(val / totalCompanies).toFixed(2);
        }

        return val;
    }

    _originalScenarioPointsCalculate = ({
        period,
        time,
        availableIdx,
        monthPeriod,
        rule,
        currentSerieId,
        points,
        currentPoint,
        opts
    } = {}) => {
        const clonedPoints = _.cloneDeep(points);
        const serie = this.Serie.findOneCollection({ _id: currentSerieId });
        if (serie.type === "constant")
            return currentPoint.c || currentPoint.v || 0;

        const ruleKey = _.get(rule, "i", false)
            ? "i"
            : !_.isEmpty(rule)
            ? "v"
            : "custom";

        const classifier = serie.classifier
            ? parseInt(String(serie.classifier).charAt(0))
            : "";

        if (period !== "monthly") {
            const currentPointIndex = clonedPoints.findIndex(
                (p) => p.t.getTime() === time.getTime()
            );
            const currentIndex =
                currentPointIndex === 0 ? 1 : currentPointIndex + 1;
            const pointRelationIndex =
                currentIndex - monthPeriod <= 0
                    ? 0
                    : currentIndex - monthPeriod;

            const pointsTimeFrame = clonedPoints.slice(
                pointRelationIndex,
                currentIndex
            );

            const sumPeriodAggregate = pointsTimeFrame.reduce((sum, point) => {
                let paramsMap = {
                    i: {
                        time: point.t,
                        rule,
                        points: clonedPoints,
                        currentPoint: point.m
                    },
                    v: { rule, classifier, currentPoint: point.m, opts },
                    custom: { currentPoint: point.m }
                };

                if (
                    this.availableMonthsIndex[period][availableIdx].includes(
                        parseInt(DateUtils.dateToString(point.t, "M"))
                    )
                ) {
                    sum =
                        sum +
                        this.originalRulesProxy[ruleKey](paramsMap[ruleKey]);
                }

                return sum;
            }, 0);

            return sumPeriodAggregate;
        }

        let paramsMap = {
            i: { time, rule, points: clonedPoints, currentPoint },
            v: { rule, classifier, currentPoint, opts },
            custom: { currentPoint }
        };

        return this.originalRulesProxy[ruleKey](paramsMap[ruleKey]);
    };

    _customScenarioPointsCalculate = ({
        period,
        time,
        availableIdx,
        monthPeriod,
        rule,
        currentSerieId,
        points,
        currentPoint,
        isAccumulated,
        opts
    } = {}) => {
        const clonedPoints = _.cloneDeep(points);
        const serie = this.Serie.findOneCollection({ _id: currentSerieId });

        if (serie.type === "constant")
            return currentPoint.c || currentPoint.v || 0;

        const classifier = serie.classifier
            ? parseInt(String(serie.classifier).charAt(0))
            : "";

        if (isAccumulated && _.get(rule, "v") === "f") {
            if (
                classifier >= 3 &&
                (opts.rootAccumulated || !opts.ignoreAccountResult)
            )
                return this._scenarioAccumulated(time, clonedPoints);
            else return currentPoint["v"];
        }

        const ruleKey = _.get(rule, "i", false)
            ? "i"
            : !_.isEmpty(rule)
            ? "v"
            : "custom";

        if (period !== "monthly") {
            const currentPointIndex = clonedPoints.findIndex(
                (p) => p.t.getTime() === time.getTime()
            );
            const currentIndex =
                currentPointIndex === 0 ? 1 : currentPointIndex + 1;
            const pointRelationIndex =
                currentPointIndex - monthPeriod <= 0
                    ? 0
                    : currentPointIndex - monthPeriod;

            const pointsTimeFrame = clonedPoints.slice(
                pointRelationIndex,
                currentIndex
            );

            const sumPeriodAggregate = pointsTimeFrame.reduce((sum, point) => {
                let paramsMap = {
                    i: {
                        time: point.t,
                        rule,
                        isAccumulated,
                        points: clonedPoints,
                        currentPoint: point.m
                    },
                    v: {
                        time,
                        rule,
                        points: clonedPoints,
                        currentPoint: point.m
                    },
                    custom: { currentPoint: point.m }
                };

                if (
                    this.availableMonthsIndex[period][availableIdx].includes(
                        parseInt(DateUtils.dateToString(point.t, "M"))
                    )
                ) {
                    sum =
                        sum +
                        this.scenarioRulesProxy[ruleKey](paramsMap[ruleKey]);
                }

                return sum;
            }, 0);

            return sumPeriodAggregate;
        }

        let paramsMap = {
            i: {
                time,
                rule,
                isAccumulated,
                points: clonedPoints,
                currentPoint
            },
            v: { time, rule, points: clonedPoints, currentPoint },
            custom: { currentPoint }
        };

        return this.scenarioRulesProxy[ruleKey](paramsMap[ruleKey]);
    };

    _originalTemporalRuleFunction = ({
        time,
        rule,
        points,
        currentPoint
    } = {}) => {
        if (parseInt(DateUtils.dateToString(time, "M")) === 1)
            return currentPoint["v"] || 0;

        const previousPoint = _.find(points, {
            t: new Date(CalcOperation.helperMonths(time, rule.i))
        });

        if (!previousPoint) return _.get(currentPoint["v"], 0);
        else return _.get(previousPoint, "m.v", 0);
    };

    _originalValueRuleFunction = ({
        rule,
        classifier,
        currentPoint,
        opts
    } = {}) => {
        if (
            opts.ignoreAccountResult &&
            classifier >= 3 &&
            _.isNumber(currentPoint.f) &&
            _.isNumber(currentPoint.o)
        )
            return currentPoint.f - currentPoint.o;
        else return _.isNumber(currentPoint[rule.v]) ? currentPoint[rule.v] : 0;
    };

    _originalDefaultRuleFunction = ({ currentPoint } = {}) => {
        if (_.isNumber(currentPoint.f) && _.isNumber(currentPoint.o)) {
            return currentPoint.f - currentPoint.o;
        } else
            return _.isNumber(currentPoint.f)
                ? currentPoint.f || 0
                : currentPoint.v || 0;
    };

    _scenarioTemporalRuleFunction = ({
        time,
        rule,
        isAccumulated,
        points,
        currentPoint
    } = {}) => {
        if (!isAccumulated) {
            if (parseInt(DateUtils.dateToString(time, "M")) === 1)
                return _.get(currentPoint, "v", 0);

            const previousValue = this._reducePrev(points, time);

            const previousPoint = _.find(points, {
                t: new Date(CalcOperation.helperMonths(time, rule.i))
            });

            if (!previousPoint && previousValue) {
                return _.get(currentPoint, "v", 0) - previousValue || 0;
            } else if (!previousPoint && !previousValue) {
                return _.get(currentPoint, "v", 0);
            } else
                return (
                    _.get(currentPoint, "v", 0) - _.get(previousPoint, "m.v", 0)
                );
        } else return _.get(currentPoint, "v", 0);
    };

    _scenarioValueRuleFunction = ({
        time,
        rule,
        points,
        currentPoint
    } = {}) => {
        if (_.get(rule, "v") === "o") {
            const prevP = _.find(points, {
                t: CalcOperation.helperMonths(time, -1)
            });

            return prevP ? prevP["v"] : 0;
        } else return currentPoint["v"] || 0;
    };

    _scenarioDefaultRuleFunction = ({ currentPoint } = {}) => {
        return _.isNumber(currentPoint["v"]) ? currentPoint["v"] : 0;
    };

    /**
     * Method that calculates the accumulated value of a scenario
     * @param {Date} t date
     * @param {Array} point array of points
     * @param {String} key scenario key
     */
    _scenarioAccumulated(time, points) {
        const pointsFiltered = points.filter((p) => {
            return (
                DateUtils.getFullYear(time) === DateUtils.getFullYear(p.t) &&
                p.t.getTime() <= time.getTime()
            );
        });

        const ret = pointsFiltered.reduce((total, current) => {
            return (total += _.get(current, "m.v", 0));
        }, 0);

        return ret;
    }

    _reducePrev(points, time) {
        return points.reduce((total, value) => {
            if (
                value.t < time &&
                DateUtils.getDate(value.t).getFullYear() === time.getFullYear()
            ) {
                total += value["m"]["v"] || 0;
            }
            return total;
        }, 0);
    }
}
